import * as React from 'react';
import { usePriceTiersBlock } from '@ids/context';
import { PropsWithChildrenOnly } from '@ids/types/utility';
import { SerializedPriceTier } from '@ids/types/now-api';

const TabsStateContext = React.createContext<SerializedPriceTier[]>([]);

const TabsDispatchContext = React.createContext<{
    selectTab: (selectIndex: number) => void;
}>({ selectTab: () => null });

const TabsProvider = ({ children }: PropsWithChildrenOnly) => {
    const uncontrolledTabs = usePriceTiersBlock()?.priceTiers ?? [];
    const [tabs, setTabs] = React.useState(uncontrolledTabs);

    const selectTab = React.useCallback(
        (selectIndex: number) => {
            const updatedTabs = tabs.map((tab, index) => ({
                ...tab,
                isSelected: selectIndex === index,
            }));

            setTabs(updatedTabs);
        },
        [tabs]
    );

    const memoizedTabs = React.useMemo(() => tabs, [tabs]);

    if (!tabs.length) {
        return null;
    }

    return (
        <TabsStateContext.Provider value={memoizedTabs}>
            <TabsDispatchContext.Provider value={{ selectTab }}>{children}</TabsDispatchContext.Provider>
        </TabsStateContext.Provider>
    );
};

const useTabsState = () => React.useContext(TabsStateContext);
const useTabsDispatch = () => React.useContext(TabsDispatchContext);

export { TabsProvider, useTabsState, useTabsDispatch };
